import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useTranslation } from "@/hooks/useTranslation";
import { cn } from "@/lib/utils";
import {
  ColumnDef,
  ColumnFiltersState,
  FilterFn,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Skeleton } from "../ui/skeleton";
import { DataTablePagination } from "./data-table-pagination";
import { DataTableToolbar } from "./data-table-toolbar";

/**
 * Global filter function that searches across all row data
 * Case-insensitive search
 */
const globalFilterFn: FilterFn<any> = (row, _columnId, filterValue) => {
  const search = filterValue.toLowerCase();

  // Get all values from the row
  const rowValues = Object.values(row.original).filter(
    (value) => value !== null && value !== undefined
  );

  // Search in all string values
  return rowValues.some((value) => {
    if (typeof value === "string") {
      return value.toLowerCase().includes(search);
    }
    if (typeof value === "number") {
      return value.toString().includes(search);
    }
    if (typeof value === "boolean") {
      return value.toString().includes(search);
    }
    return false;
  });
};

export type DataTableOrderByProps = Record<string, "DESC" | "ASC">[];

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  isLoading?: boolean;
  isLoadingStreaming?: boolean;
  pagination?: boolean;
  pageSize?: number;
  showLinesPerPage?: boolean;
  showFilter?: boolean;
  showTotalCounter?: boolean;
  emptyText?: string | null;
  rowClassNames?: (row: TData) => string;
  enabledListingFeature?: boolean;
  sortingState?: SortingState;
  columnsPersistenceKey?: string;
  onPaginationChange?: (pageIndex: number, pageSize: number) => void;
  onSortedDataChange?: (sortedData: TData[]) => void;
  toolbarContent?: React.ReactNode;
  additionalFilters?: React.ReactNode;
  /** Number of columns to pin from the left (e.g., 4 pins first 4 columns) */
  stickyColumns?: number;
  /** Pin the last column to the right edge */
  stickyRightColumn?: boolean;
}

export function DataTable<TData, TValue>({
  columns,
  data,
  isLoading = false,
  isLoadingStreaming = false,
  pagination = true,
  pageSize = 25,
  showLinesPerPage = true,
  showFilter = true,
  showTotalCounter = true,
  emptyText = null,
  rowClassNames = () => "",
  enabledListingFeature = false,
  sortingState = [],
  columnsPersistenceKey = undefined,
  onPaginationChange = () => {},
  onSortedDataChange,
  toolbarContent = null,
  additionalFilters = <></>,
  stickyColumns = 0,
  stickyRightColumn = false,
}: DataTableProps<TData, TValue>) {
  const { t } = useTranslation();
  const [rowSelection, setRowSelection] = useState({});
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [globalFilter, setGlobalFilter] = useState("");
  const [sorting, setSorting] = useState<SortingState>(sortingState);

  // Use ref to avoid infinite loop with callback in dependencies
  const onSortedDataChangeRef = useRef(onSortedDataChange);
  useEffect(() => {
    onSortedDataChangeRef.current = onSortedDataChange;
  }, [onSortedDataChange]);

  // Sticky columns: track header cell refs to measure widths
  const headerRefs = useRef<(HTMLTableCellElement | null)[]>([]);
  const [stickyOffsets, setStickyOffsets] = useState<number[]>([]);

  const measureStickyOffsets = useCallback(() => {
    if (stickyColumns <= 0) return;

    const offsets: number[] = [];
    let cumulativeWidth = 0;

    for (let i = 0; i < stickyColumns; i++) {
      offsets.push(cumulativeWidth);
      const cell = headerRefs.current[i];
      if (cell) {
        cumulativeWidth += cell.getBoundingClientRect().width;
      }
    }

    setStickyOffsets(offsets);
  }, [stickyColumns]);

  useLayoutEffect(() => {
    measureStickyOffsets();
  }, [measureStickyOffsets, columnVisibility]);

  useEffect(() => {
    if (stickyColumns <= 0) return;

    const resizeObserver = new ResizeObserver(() => {
      measureStickyOffsets();
    });

    headerRefs.current.slice(0, stickyColumns).forEach((ref) => {
      if (ref) resizeObserver.observe(ref);
    });

    return () => resizeObserver.disconnect();
  }, [stickyColumns, measureStickyOffsets]);

  const defaultEmptyText =
    emptyText || t("components.dataTable.table.noResults");

  const columnsFinal = useMemo(
    () =>
      enabledListingFeature === true
        ? columns
        : columns.filter((col) => col.id !== "Listes"),
    [columns, enabledListingFeature]
  );

  const dataFinal = useMemo(() => [...data], [data]);

  const table = useReactTable({
    data: dataFinal,
    columns: columnsFinal,
    state: {
      sorting,
      columnVisibility,
      rowSelection,
      columnFilters,
      globalFilter,
    },
    initialState: {
      pagination: {
        pageIndex: 0,
        pageSize: pagination ? pageSize : 999,
      },
    },
    autoResetPageIndex: false,
    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onColumnVisibilityChange: setColumnVisibility,
    globalFilterFn: globalFilterFn,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
  });

  const tableState = table.getState();

  useEffect(() => {
    if (columnsPersistenceKey && localStorage.getItem(columnsPersistenceKey)) {
      setColumnVisibility(
        JSON.parse(localStorage.getItem(columnsPersistenceKey) || "")
      );
    } else {
      const columnIds = columnsFinal.map((col) => col.id);
      const initialColumnVisibility = columnsFinal
        // @ts-expect-error complicated typage
        .filter((col) => col.initialVisibility === false)
        .map((col) => col.id)
        .reduce((visibilityState, columnId) => {
          // @ts-expect-error complicated typage
          visibilityState[columnId] = !columnIds.includes(columnId);
          return visibilityState;
        }, {});

      setColumnVisibility(initialColumnVisibility);
    }
  }, []);

  useEffect(() => {
    if (columnsPersistenceKey && columnVisibility) {
      localStorage.setItem(
        columnsPersistenceKey,
        JSON.stringify(columnVisibility)
      );
    }
  }, [columnVisibility]);

  // `autoResetPageIndex` is off so a reveal or a streamed row never moves the
  // reader, but rows can also shrink under the page they read — a text filter
  // typed on page 4, fewer rows handed back. Past the last page the body is
  // empty and the pagination hides itself, so the reader is moved back to the
  // last page left. Not while `isLoading`: the rows are hidden behind the
  // skeleton and not there yet, not gone. Rows streaming in
  // (`isLoadingStreaming`, as every search table passes its loading) are on
  // screen as they come, so a shrink there is real — a filter typed while the
  // table fills, a new search starting from no rows — and the reader follows
  // it at once instead of reading an empty page until the stream ends.
  const pageCount = table.getPageCount();
  useEffect(() => {
    if (isLoading) return;
    const { pageIndex } = tableState.pagination;
    if (pageIndex > 0 && pageIndex >= pageCount) {
      table.setPageIndex(Math.max(pageCount - 1, 0));
    }
  }, [isLoading, pageCount, table, tableState.pagination]);

  useEffect(() => {
    onPaginationChange(
      tableState.pagination.pageIndex,
      tableState.pagination.pageSize
    );
  }, [
    onPaginationChange,
    tableState.pagination.pageIndex,
    tableState.pagination.pageSize,
  ]);

  // Notify parent of sorted/filtered data changes
  useEffect(() => {
    if (onSortedDataChangeRef.current && table) {
      const sortedRows = table.getSortedRowModel().rows;
      const sortedData = sortedRows.map((row) => row.original);
      onSortedDataChangeRef.current(sortedData);
    }
  }, [sorting, columnFilters, globalFilter, dataFinal, table]);

  return (
    <div className="space-y-4">
      {showFilter && (
        <DataTableToolbar
          table={table}
          additionalActions={toolbarContent}
          additionalFilters={additionalFilters}
          disabled={isLoading}
        />
      )}
      <div className="overflow-hidden rounded-xl border bg-background">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header, index) => {
                  const isSticky = index < stickyColumns;
                  const isLastSticky = index === stickyColumns - 1;
                  const isStickyRight =
                    stickyRightColumn &&
                    index === headerGroup.headers.length - 1;
                  return (
                    <TableHead
                      key={header.id}
                      colSpan={header.colSpan}
                      ref={(el) => {
                        if (isSticky) headerRefs.current[index] = el;
                      }}
                      className={cn(
                        isSticky && "sticky z-20 bg-muted",
                        isLastSticky &&
                          "after:absolute after:right-0 after:top-0 after:h-full after:w-px after:bg-border",
                        isStickyRight &&
                          "sticky right-0 z-20 bg-muted before:absolute before:left-0 before:top-0 before:h-full before:w-px before:bg-border"
                      )}
                      style={
                        isSticky
                          ? { left: stickyOffsets[index] ?? 0 }
                          : undefined
                      }
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {(isLoading || isLoadingStreaming) &&
              [...Array(5)].map((_, index) => (
                <TableRow key={index}>
                  <TableCell colSpan={columns.length}>
                    <Skeleton className="my-1 h-4" />
                  </TableCell>
                </TableRow>
              ))}

            {(!isLoading || isLoadingStreaming) &&
            table.getRowModel().rows?.length
              ? table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    className={cn(
                      "group transition-colors",
                      rowClassNames(row.original)
                    )}
                    data-state={row.getIsSelected() && "selected"}
                  >
                    {row.getVisibleCells().map((cell, index) => {
                      const isSticky = index < stickyColumns;
                      const isLastSticky = index === stickyColumns - 1;
                      const isStickyRight =
                        stickyRightColumn &&
                        index === row.getVisibleCells().length - 1;
                      return (
                        <TableCell
                          key={cell.id}
                          className={cn(
                            isSticky &&
                              "sticky z-10 bg-background group-hover:bg-muted transition-colors",
                            isLastSticky &&
                              "after:absolute after:right-0 after:top-0 after:h-full after:w-px after:bg-border",
                            isStickyRight &&
                              "sticky right-0 z-10 bg-background group-hover:bg-muted transition-colors before:absolute before:left-0 before:top-0 before:h-full before:w-px before:bg-border"
                          )}
                          style={
                            isSticky
                              ? { left: stickyOffsets[index] ?? 0 }
                              : undefined
                          }
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))
              : !isLoading &&
                !isLoadingStreaming && (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length}
                      className="h-24 text-center"
                    >
                      {defaultEmptyText}
                    </TableCell>
                  </TableRow>
                )}
          </TableBody>
        </Table>
      </div>
      <div className="flex flex-col gap-4 justify-between items-center sm:flex-row">
        <div className="w-full text-xs text-center text-muted-foreground sm:w-auto sm:text-left">
          {showTotalCounter && (
            <span>
              {t("components.dataTable.table.total")}{" "}
              <strong>{data.length}</strong>{" "}
              {t("components.dataTable.table.results_other", {
                count: data.length,
              })}
            </span>
          )}
        </div>
        {pagination &&
          (table.getRowModel().rows?.length > 10 ||
            table.getPageCount() > 1) && (
            <DataTablePagination
              showLinesPerPage={showLinesPerPage}
              table={table}
            />
          )}
      </div>
    </div>
  );
}
